import React, { useState } from "react";
import "./OceShaFaq.css";

interface FaqItem {
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    question: "How fast can I create a course?",
    answer:
      "OceSha can create a complete course in minutes. Simply tell us what you want your course to be about, and AI builds the course structure, modules, lessons, quizzes, and learning materials for you.",
  },
  {
    question: "Do I need to have existing content?",
    answer:
      "No. You can start with nothing more than an idea or topic. If you already have books, documents, podcasts, videos, websites, or other content, you can also use that knowledge to create your course.",
  },
  {
    question: "How does OceSha use my existing content?",
    answer:
      "You control how AI uses your material. Choose Faithful to My Content to stay close to your original material, Balanced to combine your content with AI enhancements, or Expanded with AI to add more examples, insights, and depth.",
  },
  {
    question: "Does the AI actually teach my course?",
    answer:
      "Yes. Your AI Digital Twin can deliver the course, explain concepts, answer student questions, and provide an interactive learning experience—not simply display course content.",
  },
  {
    question: "Can students ask the AI questions?",
    answer:
      "Yes. Students can ask questions while learning, and the AI can respond in context without interrupting the course experience.",
  },
  {
    question: "Can the AI teach in different languages?",
    answer:
      "Yes. The AI teaching experience is multilingual, allowing students to learn and ask questions in their preferred language.",
  },
  {
    question: "Can I sell the courses I create?",
    answer:
      "Yes. You can publish your course, set your price, create a course sales page, accept payments through Stripe, enroll students, and earn revenue from your knowledge.",
  },
  {
    question: "Does OceSha help me market my course?",
    answer:
      "Yes. OceSha can generate marketing content from your course, including blogs, social posts, video clips, images, infographics, and more. You can also schedule and publish content to supported platforms.",
  },
  {
    question: "Will OceSha help people discover my course?",
    answer:
      "Yes. OceSha includes SEO and AI-engine discoverability features designed to help your courses become more visible through traditional search engines and AI platforms.",
  },
  {
    question: "Can I edit the course after AI creates it?",
    answer:
      "Yes. You remain in control. Review and edit your course before publishing and continue making changes as your course evolves.",
  },
  {
    question: "Do I need technical or course-design experience?",
    answer:
      "No. OceSha is designed to handle the course-building process for you. If you can describe what you want to teach, you can get started.",
  },
  {
    question: "Can I try it for free?",
    answer:
      "Yes. You can create your first course for free and see what OceSha creates before deciding whether to publish it.",
  },
];

interface OceShaFaqProps {
  onStartFreeClick?: () => void;
}

const OceShaFaq: React.FC<OceShaFaqProps> = ({
  onStartFreeClick,
}) => {
  const [openIndex, setOpenIndex] = useState<number>(0);

  const toggle = (idx: number) => {
    setOpenIndex((prev) =>
      prev === idx ? -1 : idx
    );
  };

  return (
    <section
      className="ocfaq-section"
      aria-labelledby="ocfaq-title"
    >
      {/* =====================================================
          MAIN CARD
          ===================================================== */}

      <div className="ocfaq-card">

        <div className="ocfaq-content">

          {/* =================================================
              EYEBROW
              ================================================= */}

          <div className="ocfaq-eyebrow">
            Everything you need to know
          </div>


          {/* =================================================
              HEADING
              ================================================= */}

          <h2
            id="ocfaq-title"
            className="ocfaq-h2"
          >
            Frequently Asked{" "}
            <span className="ocfaq-gradient">
              Questions
            </span>
          </h2>


          {/* =================================================
              INTRO
              ================================================= */}

          <p className="ocfaq-intro">
            Everything you need to know about creating,
            teaching, selling, and marketing courses
            with OceSha.
          </p>


          {/* =================================================
              FAQ
              ================================================= */}

          <div className="ocfaq-grid">

            {faqItems.map((item, idx) => {
              const isOpen =
                openIndex === idx;

              return (
                <div
                  className={`ocfaq-item${
                    isOpen
                      ? " ocfaq-item-open"
                      : ""
                  }`}
                  key={item.question}
                >

                  <button
                    className="ocfaq-summary"
                    onClick={() => toggle(idx)}
                    aria-expanded={isOpen}
                    type="button"
                  >
                    <span>
                      {item.question}
                    </span>

                    <span
                      className="ocfaq-toggle-icon"
                      aria-hidden="true"
                    >
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>


                  {isOpen && (
                    <div className="ocfaq-answer">
                      <p>
                        {item.answer}
                      </p>
                    </div>
                  )}

                </div>
              );
            })}

          </div>

        </div>

      </div>


      {/* =====================================================
          START FREE
          ===================================================== */}

      <div className="ocfaq-cta-row">

        <button
          className="ocfaq-cta"
          type="button"
          onClick={onStartFreeClick}
        >
          Start Free
          
        </button>

      </div>

    </section>
  );
};

export default OceShaFaq;